import React from 'react';
import {useTranslation} from 'react-i18next';
import {WideHeader} from '../../layout/wide';

interface PlacesHeaderProps {
  title: string;
  introduction: string;
  range: string;
  totalCount: number;
  zoomedIn: boolean;
  withDescriptions: boolean;
  columnsCount: number;
  showMap: boolean;
  onToggleZoom: (zoomedIn: boolean) => void;
  onToggleDescriptions: (withDescriptions: boolean) => void;
  onChangeColumns: (columns: number) => void;
  onToggleMap: (showMap: boolean) => void;
}

interface ActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  icon: string;
  title: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  disabled = false,
  icon,
  title
}) => {
  return (
    <button
      className="bg-gray-100 hover:bg-gray-200 p-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-8 h-10"
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      <i className={`text-gray-700 fa ${icon}`} />
    </button>
  );
};

const PlacesHeader: React.FC<PlacesHeaderProps> = ({
  title,
  introduction,
  range,
  totalCount,
  zoomedIn,
  withDescriptions,
  columnsCount,
  showMap,
  onToggleZoom,
  onToggleDescriptions,
  onChangeColumns,
  onToggleMap
}) => {
  const {t} = useTranslation();
  const MIN_COLUMNS = 2;
  const MAX_COLUMNS = 8;

  const actions = (
    <>
      <ActionButton
        onClick={() => onToggleDescriptions(!withDescriptions)}
        icon={withDescriptions ? 'fa-file' : 'fa-file-lines'}
        title={withDescriptions ? t('gallery.header.actions.hideDescriptions') : t('gallery.header.actions.showDescriptions')}
      />
      <ActionButton
        onClick={() => onToggleZoom(!zoomedIn)}
        icon={zoomedIn ? 'fa-compress' : 'fa-expand'}
        title={zoomedIn ? t('gallery.header.actions.zoomOut') : t('gallery.header.actions.zoomIn')}
      />
      <ActionButton
        onClick={() => onChangeColumns(Math.min(MAX_COLUMNS, columnsCount + 2))}
        disabled={columnsCount === MAX_COLUMNS}
        icon="fa-table-cells"
        title={t('gallery.header.actions.moreColumns')}
      />
      <ActionButton
        onClick={() => onChangeColumns(Math.max(MIN_COLUMNS, columnsCount - 2))}
        disabled={columnsCount === MIN_COLUMNS}
        icon="fa-table-cells-large"
        title={t('gallery.header.actions.fewerColumns')}
      />
      <ActionButton
        onClick={() => onToggleMap(!showMap)}
        icon={showMap ? 'fa-list-ul' : 'fa-map-location-dot'}
        title={showMap ? t('places.header.actions.hideMap') : t('places.header.actions.showMap')}
      />
    </>
  );

  return (
    <WideHeader
      title={title}
      introduction={introduction}
      caption={t('gallery.header.showingResults', {range, totalCount})}
      actions={actions}
    />
  );
};

export default PlacesHeader;

